import DocumentDataCard from "src/views/datasets/components/DocumentDataCard";
import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import React, { useEffect } from "react";
import SourceDataRecordChunkCard from "./SourceDataRecordChunkCard";
import ImageSourceCard from "./ImageSourceCard";
import { isImageSourceResult } from "../../utils/imageSourceUtils";
import { useUIStore } from "../../stores/UIStore";

type TSourcesChunkView = {
  sources: any[];
  /**
   * Display/scroll index of the FIRST source in ``sources`` (0-based). The
   * three-tier panel renders one source per view, so it passes the source's
   * position in its tier here — otherwise every card would be "1" (badge) and
   * ``source-1`` (DOM id), which breaks citation scroll-targeting for [2]+.
   */
  baseIndex?: number;
  /**
   * Prefix for each card's DOM id. Citations scroll to ``source-N``, so only
   * the Cited tier uses the default "source"; other tiers (Read) pass a
   * distinct prefix so their cards don't shadow the Cited anchors in the DOM.
   */
  idPrefix?: string;
};

const SourcesChunkView = ({
  sources,
  baseIndex = 0,
  idPrefix = "source",
}: TSourcesChunkView) => {
  const scrollTargetSource = useUIStore((s) => s.scrollTargetSource);
  const setScrollTargetSource = useUIStore((s) => s.setScrollTargetSource);

  // Drive scroll-into-view from a layout-aware effect rather than the
  // setTimeout-poll loop scrollToSource used to do. The cards live in the
  // same tree this effect runs in, so by the time the effect fires React
  // has mounted them and (after a double rAF) layout has settled — we get
  // the correct scrollable ancestor without guessing or racing.
  useEffect(() => {
    if (scrollTargetSource == null) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = document.getElementById(`source-${scrollTargetSource}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // Only clear on success — if the card hasn't mounted yet, keep the
          // signal so the effect can re-fire when sources arrive.
          setScrollTargetSource(null);
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [scrollTargetSource, sources.length, setScrollTargetSource]);

  return (
    <div data-testid="aa-src-list">
      {!sources.length && (
        <NoContentCard
          title="No Sources Identified"
          description="Source material references will appear here once identified in the responses."
        />
      )}

      {sources.map((source, index) => {
        // Position within the overall source list (1-based) — drives the card's
        // number badge and its citation-scroll DOM id.
        const cardIndex = baseIndex + index + 1;
        const cardDomId = `${idPrefix}-${cardIndex}`;
        // Check for image source first (VLM-extracted images from Epic OCR)
        const isImageSource = isImageSourceResult(source);

        // Determine the source type more explicitly
        const fileType = source?.source_data_record?.data?.file_type;
        const hasFileName = !!source?.source_data_record?.data?.file_name;
        const datasetTypeName =
          source?.dataset?.dataset_type?.name?.toLowerCase();

        // Determine what type of source this is
        const isTabular =
          fileType === "text/csv" || datasetTypeName === "tabular";

        const isDocument =
          hasFileName &&
          !isTabular &&
          !isImageSource &&
          (datasetTypeName === "document" || !datasetTypeName);

        // Create a transformed copy for tabular data (don't mutate original)
        let displaySource = source;
        if (isTabular && source?.data_chunk?.chunk_data?.data) {
          try {
            const parsedData = JSON.parse(source.data_chunk.chunk_data.data);
            const rowData = parsedData.data?.[0] || {};

            // Create a NEW source object with ONLY the parsed CSV row data
            displaySource = {
              ...source,
              source_data_record: {
                ...source.source_data_record,
                data: rowData, // Just the row data, nothing else
                file_name: source.source_data_record?.data?.file_name,
                dataset_type: "tabular",
              },
            };
          } catch (error) {
            console.error("Error parsing tabular data:", error);
          }
        }

        return (
          <React.Fragment key={index}>
            {/* Image source card - VLM-extracted images */}
            {isImageSource && (
              <ImageSourceCard
                source={source}
                index={cardIndex}
                domId={cardDomId}
              />
            )}
            {/* Document source card */}
            {!isImageSource && isDocument && !isTabular && (
              <DocumentDataCard
                type="search"
                datasetId={
                  displaySource?.dataset?.id ??
                  displaySource?.source_data_record?.dataset_fk
                }
                __source_uid={displaySource?.source_data_record?.source_uid}
                chunk_data={displaySource?.data_chunk?.chunk_data}
                chunk_id={displaySource?.data_chunk.id}
                chunk_metadata={displaySource?.data_chunk?.chunk_metadata}
                chunk_create_date={displaySource?.data_chunk?.create_date}
                sdr_id={displaySource?.source_data_record?.id}
                sdr_create_date={displaySource?.source_data_record?.create_date}
                sdr_data={displaySource?.source_data_record?.data}
                scrollAndView={false}
                pageNumberCallback={() => {}}
                index={cardIndex}
                domId={cardDomId}
              />
            )}
            {/* Other source types */}
            {!isImageSource && !isDocument && (
              <SourceDataRecordChunkCard
                source={displaySource}
                index={cardIndex}
                domId={cardDomId}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SourcesChunkView;
